/**
 * Per-event embed list for Team Settings → Video (FR-VIDEO-04/05).
 *
 * Search, provider filter, sortable columns and pagination all live in the URL
 * (see `use-video-settings.ts`), so a refresh restores the view. Clearing an
 * event's video is destructive, so it goes through a confirmation that states
 * what fans and coaches will see afterwards.
 */
import { useState } from 'react';

import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DataTable, type DataTableColumn } from '../../components/data-table';
import { DeskSection } from '../../components/desk-section';
import { ApiError } from '../../lib/api-error';
import type {
  EventVideoEmbed,
  EventVideoEmbedSortField,
  VideoProviderCapability,
} from '../../lib/api-types';

import { ProviderBadge } from './provider-badge';
import type { VideoSettingsController } from './use-video-settings';

function formatDate(value: string | null): string {
  if (value === null) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

export function EventEmbedTable({
  controller,
  providers,
}: {
  controller: VideoSettingsController;
  providers: readonly VideoProviderCapability[];
}): JSX.Element {
  const { showToast } = useToast();
  const [pending, setPending] = useState<EventVideoEmbed | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  async function clearEmbed(embed: EventVideoEmbed): Promise<void> {
    setDialogError(null);
    try {
      await controller.removeEmbed(embed.event_id, embed.provider);
      setPending(null);
      showToast('Event video cleared.', 'success');
    } catch (caught) {
      setDialogError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event video could not be cleared. Please try again.'
      );
    }
  }

  const columns: readonly DataTableColumn<EventVideoEmbed>[] = [
    {
      id: 'event_name',
      header: 'Event',
      isSortable: true,
      isPinned: true,
      minWidth: 180,
      maxWidth: 320,
      grow: 2,
      render: (row) => row.event_name ?? 'Untitled event',
    },
    {
      id: 'scheduled_at',
      header: 'Scheduled',
      isSortable: true,
      priority: 1,
      minWidth: 120,
      maxWidth: 150,
      grow: 0,
      render: (row) => formatDate(row.scheduled_at),
    },
    {
      id: 'provider',
      header: 'Provider',
      priority: 2,
      minWidth: 160,
      maxWidth: 200,
      grow: 0,
      render: (row) => {
        const capability = providers.find((entry) => entry.provider_name === row.provider);
        return capability === undefined ? row.provider : <ProviderBadge provider={capability} />;
      },
    },
    {
      id: 'embed',
      header: 'Embed',
      priority: 3,
      minWidth: 200,
      grow: 3,
      renderDetail: (row) =>
        row.embed_url ?? (row.stream_id === null ? '—' : `Stream ${row.stream_id}`),
      render: (row) => (
        <span className="team-settings__embed-value">
          {row.embed_url ?? (row.stream_id === null ? '—' : `Stream ${row.stream_id}`)}
        </span>
      ),
    },
    {
      id: 'updated_at',
      header: 'Updated',
      isSortable: true,
      priority: 4,
      minWidth: 120,
      maxWidth: 150,
      grow: 0,
      render: (row) => formatDate(row.updated_at),
    },
  ];

  return (
    <DeskSection
      title="Event video"
      description="Events with their own video. Any other event falls back to the team-wide embed."
      className="team-settings__embeds"
    >
      <DataTable<EventVideoEmbed>
        caption="Per-event video embeds"
        surface="event-embeds"
        layoutName="Event video columns"
        columns={columns}
        rows={controller.embeds}
        getRowId={(row) => `${row.event_id}:${row.provider}`}
        sort={{ by: controller.query.sort, order: controller.query.direction }}
        onSortChange={(sort) =>
          controller.setQuery({
            sort: sort.by as EventVideoEmbedSortField,
            direction: sort.order,
          })
        }
        search={{
          value: controller.query.search,
          onChange: (value) => controller.setQuery({ search: value }),
          label: 'Search events',
          placeholder: 'Search by event name',
        }}
        filters={[
          {
            id: 'provider',
            label: 'Provider',
            value: controller.query.provider,
            options: [
              { value: 'all', label: 'All providers' },
              { value: 'hudl', label: 'Hudl' },
              { value: 'youtube', label: 'YouTube Live' },
            ],
            onChange: (value) =>
              controller.setQuery({ provider: value as 'all' | 'hudl' | 'youtube' }),
            defaultValue: 'all',
          },
        ]}
        pagination={{
          page: controller.embedMeta?.page ?? 1,
          pageSize: controller.embedMeta?.limit ?? 10,
          total: controller.embedMeta?.total ?? 0,
          onPageChange: (page) => controller.setQuery({ page }),
        }}
        isLoading={controller.isEmbedsLoading}
        error={controller.embedsError}
        onRetry={controller.reload}
        emptyTitle="No event video configured"
        emptyDescription="Add an embed URL or stream id for an event above and it will appear here."
        primaryRowAction={(row) => (
          <button
            type="button"
            className="btn btn--ghost btn--sm team-settings__quiet-danger"
            onClick={() => setPending(row)}
          >
            Clear
          </button>
        )}
        actionsWidth={80}
      />

      {pending === null ? null : (
        <ConfirmDialog
          title="Clear event video?"
          tone="danger"
          consequence={`The embed for “${pending.event_name ?? 'this event'}” will be removed. Anyone opening Live Video for it will see “video has not been set up for this game yet” until it is configured again.`}
          confirmLabel="Clear video"
          error={dialogError}
          onConfirm={() => void clearEmbed(pending)}
          onCancel={() => {
            setPending(null);
            setDialogError(null);
          }}
        />
      )}
    </DeskSection>
  );
}
